import React, { useState, /* useEffect, */ useMemo } from 'react';
import { Card, CardContent } from '~/components/ui/card';
import { Dialog, DialogTrigger } from '~/components/ui/dialog';
import { AlertTriangle, CheckCircle, Heart, Loader } from 'lucide-react'; // Heart is already imported
import { cn } from '~/lib/utils';
import {
  useFavoritesStore,
  useIsFavoritesLoaded,
} from '~/stores/favorites-store'; // Import store hook and loaded status hook
import {
  STRUCTURAL_CONFIGS,
  COLOR_CONFIGS,
  type CombinedTheme,
} from './theme-config';
import {
  DomainCardContent,
  type AvailabilityStatus,
} from './domain-card-content';
const DomainDialog = React.lazy(() => import('../domain-dialog/domain-dialog'));
import type { Tld } from '~/config/tld-config';
import { useTranslation } from 'react-i18next';

export interface DomainCardProps {
  domainName: string; // Domain name without TLD
  selectedTld: string; // The selected TLD
  availabilityStatus?: AvailabilityStatus; // Status now includes 'checking'
  variations?: Array<{
    // Other TLD variations for this domain
    name: string;
    available?: boolean;
    error?: boolean;
    selected?: boolean;
  }>;
  selectedTlds?: Tld[]; // Available TLDs for the dialog
}

export function DomainCard({
  domainName,
  selectedTld,
  availabilityStatus,
  variations = [],
  selectedTlds,
}: DomainCardProps) {
  const { t } = useTranslation('common');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isStoreLoaded = useIsFavoritesLoaded(); // Check if the favorites store is hydrated

  // Select actions and the specific favorite status for this domain
  const toggleFavorite = useFavoritesStore((state) => state.toggleFavorite);
  // Directly select the boolean status for this domainName
  // This component will now re-render if this specific value changes in the store
  const isCurrentlyFavorite = useFavoritesStore((state) =>
    state.isFavorite(domainName)
  );

  // Add debug logging to see what props the card is receiving
  /* console.log(`[DomainCard] Rendering domain: ${domainName}`, {\
    selectedTld,\
    availabilityStatus,\
    variationsCount: variations.length,\
    variationsStatus: variations.map((v) => ({\
      name: v.name,\
      available: v.available,\
      error: v.error,\
    })),\
  }); */

  // No longer need the separate isFavorite function call here
  // const isCurrentlyFavorite = isStoreLoaded ? isFavorite(domainName) : false; // Removed

  // Theme selection combining structure and colors using two hashes
  const theme: CombinedTheme = useMemo(() => {
    if (!domainName) {
      // Fallback to the first structure and first color config
      return { ...STRUCTURAL_CONFIGS[0], ...COLOR_CONFIGS[0] };
    }

    // Hash for structure
    const structureHash = fnv1aHash(domainName);
    const structureIndex = structureHash % STRUCTURAL_CONFIGS.length;
    const structureConfig = STRUCTURAL_CONFIGS[structureIndex];

    // Hash for colors (with salt)
    const colorHash = fnv1aHash(domainName + '_colors');
    const colorIndex = colorHash % COLOR_CONFIGS.length;
    const colorConfig = COLOR_CONFIGS[colorIndex];

    // Combine structure and colors
    return { ...structureConfig, ...colorConfig };
  }, [domainName]);

  const handleToggleFavorite = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();

    // Only toggle if the store is loaded
    if (!isStoreLoaded) {
      console.warn('[DomainCard] Favorites store not loaded, cannot toggle.');
      return;
    }

    const isAvailableForFavoriting = availabilityStatus === 'available';

    // Get TLD list from variations if available
    const additionalTlds =
      variations.length > 0
        ? variations.map((v) => v.name.split('.')[1]).filter(Boolean) // Ensure TLDs are valid strings
        : undefined;

    // Use the standardized toggleFavorite function from the store
    toggleFavorite(
      domainName,
      selectedTld,
      isAvailableForFavoriting,
      additionalTlds
    );
  };

  // Determine the final status to pass down
  const finalAvailabilityStatus: AvailabilityStatus = availabilityStatus;

  // Log the final status that will be used for rendering
  /* console.log(\
    `[DomainCard] ${domainName}: Final status: ${finalAvailabilityStatus}`\
  ); */

  // Styling flags based on status
  const isUnavailable = finalAvailabilityStatus === 'unavailable';
  const isError = finalAvailabilityStatus === 'error';
  const showSubduedStyling = isUnavailable || isError; // Used for elements like the heart icon

  // Create a full domain object for compatibility with dialog
  const domainObj = {
    baseName: domainName,
    variations:
      variations.length > 0
        ? variations
        : [
            {
              name: `${domainName}.${selectedTld}`,
              available: availabilityStatus === 'available',
              error: availabilityStatus === 'error',
              selected: true,
            },
          ],
    checkingAvailability: finalAvailabilityStatus === 'checking',
  };

  // Log the domain object\'s checking status to verify it matches the status
  /* console.log(\
    `[DomainCard] ${domainName}: domainObj.checkingAvailability = ${domainObj.checkingAvailability}`\
  ); */

  // Correction: The selectedTld prop might already contain the dot.
  // Let's check and ensure only one dot is present.
  const displayTld = selectedTld.startsWith('.')
    ? selectedTld
    : `.${selectedTld}`;

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Card
          className={cn(
            'flex h-28 cursor-pointer flex-col items-center justify-center overflow-hidden p-0 transition-all duration-300',
            // Apply specific styles based on status
            isError
              ? 'border-red-600 bg-gradient-to-br from-red-600 to-red-800' // Error styling (red)
              : isUnavailable
                ? 'border-gray-600 bg-gradient-to-br from-gray-600 to-gray-800' // Unavailable styling (gray)
                : `${theme.gradientDirection} ${theme.bg} ${theme.border}`, // Default theme styling
            'relative border-2 shadow-md hover:scale-105 sm:h-40'
          )}
          data-card-theme={
            theme.style.includes('uppercase')
              ? 'uppercase'
              : theme.font.replace(/font-\['|\]/g, '').replace(/_/g, ' ')
          }
        >
          {/* DEV ONLY: Debug Status */}
          {import.meta.env.DEV && (
            <div className="absolute bottom-1 left-1 z-30 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[8px] text-white shadow">
              {finalAvailabilityStatus === undefined
                ? 'undefined'
                : finalAvailabilityStatus}
            </div>
          )}

          {/* Availability Badge - Top Left */}
          {finalAvailabilityStatus === 'available' && ( // Use final status
            <div
              data-testid="available-badge" // Add test ID
              className={cn(
                'absolute top-1 left-1 z-20 flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] shadow-sm sm:top-2 sm:left-2 sm:px-2 sm:text-xs',
                'border border-green-500/50 bg-green-600/90',
                'text-white'
              )}
            >
              <CheckCircle className="h-2 w-2 sm:h-3 sm:w-3" />
              <span className="font-medium">{t('domainCard.available')}</span>
            </div>
          )}
          {finalAvailabilityStatus === 'checking' && ( // Use final status
            <Loader
              className={cn(
                'absolute top-2 left-2 z-20 h-4 w-4 animate-spin',
                theme.text
              )}
            />
          )}
          {/* Error Badge - Top Left */}
          {finalAvailabilityStatus === 'error' && (
            <div
              data-testid="error-badge" // Add test ID
              className={cn(
                'absolute top-1 left-1 z-20 flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] shadow-sm sm:top-2 sm:left-2 sm:px-2 sm:text-xs',
                'border border-red-500/50 bg-red-600/90', // Red color for error badge
                'text-white'
              )}
            >
              <AlertTriangle className="h-2 w-2 sm:h-3 sm:w-3" />
              <span className="font-medium">{t('domainCard.error')}</span>{' '}
              {/* Use a generic error text */}
            </div>
          )}

          {/* Favorite Button */}
          <button
            onClick={handleToggleFavorite}
            disabled={!isStoreLoaded} // Disable button until store is loaded
            data-testid="favorite-button" // Add test ID
            className={cn(
              'absolute top-1 right-1 z-20 rounded-full p-1 transition-all sm:top-2 sm:right-2',
              !isStoreLoaded && 'cursor-not-allowed opacity-50', // Style when disabled
              isStoreLoaded && showSubduedStyling // Use combined flag for subdued states when loaded
                ? 'bg-gray-700 hover:bg-gray-600' // Keep button background subdued for both error/unavailable
                : 'bg-white/20 hover:bg-white/40'
            )}
            aria-label={
              isCurrentlyFavorite // Use context status
                ? t('domainResults.removeFromFavorites')
                : t('domainResults.addToFavorites')
            }
          >
            <Heart
              className={cn(
                'h-4 w-4 sm:h-5 sm:w-5',
                isCurrentlyFavorite // Use context status
                  ? 'fill-red-500 text-red-500'
                  : showSubduedStyling // Use combined flag for subdued states
                    ? 'text-gray-300' // Keep heart icon gray for both error/unavailable when not favorited
                    : 'text-white'
              )}
            />
          </button>

          <CardContent className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden p-3 text-center sm:p-6">
            <DomainCardContent
              tld={displayTld} // Use directly calculated tld
              theme={theme}
              domainBaseName={domainName}
              availability={finalAvailabilityStatus} // Correct prop name
              domainVariations={domainObj.variations}
            />
          </CardContent>
        </Card>
      </DialogTrigger>

      <DomainDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        domain={domainObj}
        selectedTlds={selectedTlds}
      />
    </Dialog>
  );
}

export * from './types';
export type { AvailabilityStatus } from './domain-card-content';

// Replace simpleHash (DJB2) with FNV-1a hash function for potentially better distribution
function fnv1aHash(str: string): number {
  let hash = 0x811c9dc5; // FNV offset basis (2166136261)

  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i); // XOR with character byte
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24); // FNV prime multiplication (16777619)
  }

  return hash >>> 0; // Return unsigned 32-bit integer
}
